/* eslint-disable no-nested-ternary */
import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { uuid } from '@carvana/purchase-utils';

const Disabled = css`
  cursor: not-allowed;
  opacity: 0.3 !important;
`;

const Container = styled.div`
  border-bottom: solid 1px
    ${props =>
      props.error
        ? props.theme.carvana.red
        : props.isFocused
        ? props.theme.carvana.blue
        : props.theme.carvana.grayFifteen};
  box-sizing: border-box;
  /* margin-bottom: 8px; */
  padding: 0.5rem 8px 0;
  position: relative;
  width: ${props => props.width};

  &:hover {
    border-bottom: solid 1px
      ${props =>
        props.error ? props.theme.carvana.red : props.theme.carvana.blue};
  }

  ${props => props.disabled && Disabled}
`;

const Input = styled.input`
  border: none;
  font-size: 16px;
  outline: 0;
  padding: 16px 0 15px;
  width: 100%;
  color: #555;
  background-color: ${props => props.transparent && 'transparent'};

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    transition: background-color 5000s ease-in-out 0s;
  }

  &::-webkit-input-placeholder {
    color: transparent;
  }

  &:focus::-webkit-input-placeholder {
    color: ${props => (props.error ? 'red' : '#999')};
  }

  &::-moz-placeholder {
    color: transparent;
  }
  &:focus::-moz-placeholder {
    color: ${props => (props.error ? 'red' : '#999')};
  }

  &:-ms-input-placeholder {
    color: transparent;
  }
  &:focus:-ms-input-placeholder {
    color: ${props => (props.error ? 'red' : '#999')};
  }

  &:invalid {
    box-shadow: none;
  }

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  ${props => props.disabled && Disabled}
`;

const Label = styled.label`
  font-size: 1rem;
  position: absolute;
  transform-origin: top left;
  transform: translate(0, 16px) scale(1);
  transition: all 0.3s ease-in-out;
  color: ${props => props.theme.carvana.grayThree};
  color: ${props => props.isFocused && props.theme.carvana.blue};
  color: ${props => props.error && 'red'};

  transform: ${props =>
    props.hasValue &&
    `translate(0, -${props.error.length > 35 ? 20 : 9}px) scale(0.85)`};
  text-align: ${props => props.error && 'left'};

  ${props => props.disabled && Disabled}
`;

const ShowHidePassword = styled.div`
  text-align: right;
  cursor: pointer;
  color: ${props => props.theme.carvana.blue};
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  z-index: 2;
  position: relative;

  ${props => props.disabled && Disabled}
`;

const InputTip = styled.div`
  color: ${props => props.theme.carvana.grayFour};
  font-size: 12px;
  font-style: italic;
  font-weight: 700;
  margin-top: 8px;
`;

const InputTipNote = styled.span`
  color: ${props => props.theme.carvana.orange};
`;

const EndAdornment = styled.div`
  position: absolute;
  right: 0;
  top: 2rem;
`;

class FloatInput extends PureComponent {
  inputRef = React.createRef();

  static propTypes = {
    label: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onTouched: PropTypes.func,
    onKeyDown: PropTypes.func,
    onBlur: PropTypes.func,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    type: PropTypes.string,
    value: PropTypes.string,
    maxLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    autoFocus: PropTypes.bool,
    useLocalState: PropTypes.bool,
    validate: PropTypes.func,
    endAdornment: PropTypes.oneOfType([PropTypes.node, PropTypes.elementType]),
    'data-cv-test-override': PropTypes.string
  };

  static defaultProps = {
    onTouched: null,
    onKeyDown: null,
    onBlur: null,
    error: false,
    type: 'text',
    value: '',
    maxLength: 36,
    width: '100%',
    autoFocus: false,
    useLocalState: true,
    validate: () => {},
    endAdornment: null,
    'data-cv-test-override': null
  };

  state = {
    isFocused: false,
    showPassword: false,
    id: uuid(),
    value: this.props.value
  };

  onFocus = e => {
    const { onTouched } = this.props;
    this.setState({ isFocused: true });
    if (onTouched) onTouched(e);
  };

  onBlur = () => {
    this.setState({ isFocused: false });
    const { onBlur } = this.props;
    if (onBlur) onBlur();
  };

  onChange = e => {
    e.persist();
    const { onChange, validate } = this.props;
    const { value } = e.target;
    this.numberTypeMaxLength(e);

    this.setState({ value });
    onChange(e, validate);
  };

  onKeyDown = e => {
    const { onKeyDown } = this.props;

    if (onKeyDown) {
      return onKeyDown(e);
    }

    if (e.key === 'Escape') {
      this.inputRef.current.blur();
    }

    return e;
  };

  onLabelClick = e => {
    e.preventDefault();
    this.inputRef.current.focus();
  };

  togglePassword = () => {
    this.inputRef.current.focus();
    this.setState(({ showPassword }) => ({ showPassword: !showPassword }));
  };

  numberTypeMaxLength = e => {
    if (e.target.type === 'number') {
      e.target.value = Math.max(0, parseInt(e.target.value, 10))
        .toString()
        .slice(0, e.target.maxLength);
    }
  };

  render() {
    const { isFocused, showPassword, id, value: localValue } = this.state;
    const {
      onTouched,
      useLocalState,
      endAdornment,
      'data-cv-test-override': cvDataTestOverride,
      ...rest
    } = this.props;
    const {
      label,
      error,
      type,
      width,
      disabled,
      autoFocus,
      inputTip,
      value: passedValue
    } = rest;
    const isTypePassword = type === 'password';
    const value = useLocalState ? localValue : passedValue;
    const hasValue = value && value.length > 0;
    const inputType =
      isTypePassword && !showPassword
        ? 'password'
        : isTypePassword
        ? 'text'
        : type || 'text';

    return (
      <Fragment>
        <Container error={error} width={width} isFocused={isFocused}>
          <Label
            onClick={this.onLabelClick}
            hasValue={isFocused || hasValue}
            isFocused={isFocused}
            htmlFor={id}
            error={error}
            data-cv-test={error ? 'formErrorMessage' : 'labelMessage'}
          >
            {error || label}
          </Label>

          <Input
            {...rest}
            autoFocus={autoFocus}
            id={id}
            type={inputType}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            ref={this.inputRef}
            onChange={this.onChange}
            onKeyDown={this.onKeyDown}
            error={error}
            value={value}
            data-cv-test={cvDataTestOverride || rest['data-cv-test']}
          />

          {endAdornment && <EndAdornment>{endAdornment}</EndAdornment>}
        </Container>

        {isTypePassword && (
          <ShowHidePassword disabled={disabled} onClick={this.togglePassword}>
            {showPassword ? 'Hide' : 'Show'}
          </ShowHidePassword>
        )}

        {inputTip && isFocused && (
          <InputTip>
            <InputTipNote>Note: </InputTipNote>
            {inputTip}
          </InputTip>
        )}
      </Fragment>
    );
  }
}

export default FloatInput;
